/**
 * Apply a uniform scale to the .case-frame menu so it grows on big monitors.
 *
 * Sets `--menu-scale` on :root. The CSS uses it as
 *   transform: scale(var(--menu-scale)) — gated to min-width: 1024px so
 * mobile (fullscreen) and tablet (centered card) are untouched.
 *
 * Baseline: 1440×900 maps to scale=1, so a typical 13" laptop sees no change.
 * Larger viewports scale up proportionally, capped at MAX_SCALE so a 5K
 * monitor doesn't render a comically oversized menu.
 */
const DESIGN_W = 1440;
const DESIGN_H = 900;
const MAX_SCALE = 1.6;
const DESKTOP_MIN_PX = 1024;

function computeMenuScale(viewportWidth: number, viewportHeight: number): number {
  if (viewportWidth < DESKTOP_MIN_PX) return 1;
  const sw = viewportWidth / DESIGN_W;
  const sh = viewportHeight / DESIGN_H;
  const fit = Math.min(sw, sh);
  return Math.max(1, Math.min(fit, MAX_SCALE));
}

export function setupMenuScale(): () => void {
  const apply = (): void => {
    const scale = computeMenuScale(window.innerWidth, window.innerHeight);
    document.documentElement.style.setProperty("--menu-scale", String(scale));
  };
  apply();
  window.addEventListener("resize", apply);
  window.addEventListener("orientationchange", apply);
  return () => {
    window.removeEventListener("resize", apply);
    window.removeEventListener("orientationchange", apply);
    document.documentElement.style.removeProperty("--menu-scale");
  };
}
